import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTheme, alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import { MiTHEME } from "@miview/theme";
import {
  MiTable,
  Chip,
  MiTab,
  MiLink,
  MiPageHeader,
  MiIconButton,
  MiIcon,
  MiBox,
} from "@miview/components";
import {
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import { createFilter, openInNewTab, combineFilters } from "@miview/utils";
import { accountingTransactionService } from "@miview/api";
import {
  useDebounce,
  useComponentState,
  usePayPeriods,
  useFilter,
  usePagination,
} from "@miview/hooks";

import {
  ACCOUNTPAYABLE_CHIP_STATUS_OPENBILLSTAB,
  ACCOUNTPAYABLE_CHIP_STATUS_HISTORYTAB,
  ACCOUNTPAYABLE_TAB_DATA,
  ACCOUNTPAYABLE_ACTIVETAB_VALUE,
  ACCOUNTPAYABLE_BUTTONGROUP_VALUES,
  HTTP_STATUSES,
  LEDGER_ACCOUNTS,
  EXPORT_CHIP_OPTIONS,
  PAGINATION,
} from "@miview/constants";
import moment from "moment";
import { AccountingFilterComponent } from "./accountingFilterComponent";
import { PrintPage } from "./printPage";
import { mdiArrowRight, mdiArrowLeft, mdiFormatListBulleted } from "@mdi/js";
import { PayBillsCollapse } from "./PayBillsCollapse";
import { ExportModal } from "./Modals/ExportModal";

const statusMapObj = {
  openbills: ACCOUNTPAYABLE_CHIP_STATUS_OPENBILLSTAB,
  history: ACCOUNTPAYABLE_CHIP_STATUS_HISTORYTAB,
};

const payTypes = {
  [ACCOUNTPAYABLE_BUTTONGROUP_VALUES.BONUS_PAY]: ["Bonus"],
  [ACCOUNTPAYABLE_BUTTONGROUP_VALUES.JOB_PAY]: [
    "Work Pay",
    "Work Pay - Deferred",
    "Extra",
    "Digging",
    "Payroll",
    "Additional Pay",
  ],
};

const useStyles = makeStyles()((theme) => ({
  headingWrapper: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
  },
  heading: {
    fontSize: MiTHEME.fontSize.large,
    fontWeight: MiTHEME.fontWeight.bold,
  },
  tableStyles: {
    border: 0,
    marginTop: "5px",
    marginBottom: "10px",
    "& .MuiDataGrid-window": {
      overflow: "hidden",
    },
    "& .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      padding: 0,
    },
    "& .grouped-class": {
      backgroundColor: theme.palette.primary.red,
    },
  },
}));

const EMPTY_FILTER = createFilter().getFilter();

export const MiJournalEntries = () => {
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedExportChip, setSelectedExportChip] = useState([]);
  const [selectedExport, setSelectedExport] = useState();
  const [totalSum, setTotalSum] = useState(0);
  const [activeTab, setActiveTab] = useState(
    ACCOUNTPAYABLE_ACTIVETAB_VALUE.OPENBILLS
  );
  const [filterState, setFilterState] = useState(EMPTY_FILTER);
  const [selectedButton, setSelectedButton] = useState(
    ACCOUNTPAYABLE_BUTTONGROUP_VALUES.SUBCONTRACTORS
  );
  const [paySelection, setPaySelection] = useState("");
  const [accountingTransactions, setAccountingTransactions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openBillsTabFilterState, setOpenBillsTabFilterState] = useState({
    searchTerm: "",
    statusFilter: [],
    tableFilters: {
      linkOperator: "and",
      items: [],
    },
  });
  const [historyTabFilterState, setHistoryTabFilterState] = useState({
    searchTerm: "",
    statusFilter: [],
    tableFilters: {
      linkOperator: "and",
      items: [],
    },
  });
  const [rowGroupingModel, setRowGroupingModel] = useState([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const aggregationModel = { amount: "sum" };
  const theme = useTheme();
  const { classes } = useStyles();
  const debounceFilters = useDebounce(filterState, 400);
  const stateManager = useComponentState();
  const exportFilter = useFilter({
    initialFilter: createFilter().equals({
      expenseTypeExternalAccountId: LEDGER_ACCOUNTS.ACCOUNTS_PAYABLE,
      status: statusMapObj[activeTab],
    }),
  });
  const apiRef = useGridApiRef();
  const { payPeriods, selectedMultiPayPeriod, setSelectedMultiPayPeriod } =
    usePayPeriods();

  useEffect(() => {
    getPageData();
    getTotalSum();
  }, [
    exportFilter.debounce,
    debounceFilters,
    selectedMultiPayPeriod,
    selectedStatus,
  ]);

  useEffect(() => {
    exportFilter.equals({
      status: statusMapObj[activeTab],
    });
  }, [activeTab]);

  const getTotalSum = async () => {
    if (
      activeTab === ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY &&
      !search &&
      !selectedStatus.length &&
      !selectedMultiPayPeriod.length
    ) {
      return;
    }

    const pagingFilter = createFilter();

    if (activeTab === ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY) {
      pagingFilter.equals({ payDate: selectedMultiPayPeriod });
    }

    pagingFilter.equals({
      status: selectedStatus
        ? selectedStatus
        : statusMapObj[activeTab].filter((val) => val !== "Cancelled"),
    });

    const combinedFilters = combineFilters(
      pagingFilter.getFilter(),
      combineFilters(filterState, exportFilter.getFilter())
    );

    const result = await accountingTransactionService.sum({
      params: { search: search },
      filters: combinedFilters,
    });
    if (result.status === HTTP_STATUSES.OK) {
      setTotalSum(result.data);
    }
  };

  const fetchData = ({ params, sort } = {}) => {
    if (
      activeTab === ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY &&
      !search &&
      !selectedStatus.length &&
      !selectedMultiPayPeriod.length
    ) {
      return {
        status: 200,
        data: [],
        headers: { totalcount: 0, totalpages: 0 },
      };
    }

    return stateManager.run(async () => {
      const pagingFilter = createFilter();

      if (activeTab === ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY) {
        pagingFilter.equals({ payDate: selectedMultiPayPeriod });
      }

      pagingFilter.equals({ status: selectedStatus });

      const combinedFilters = combineFilters(
        pagingFilter.getFilter(),
        combineFilters(filterState, exportFilter.getFilter())
      );

      return await accountingTransactionService.getAll(
        params,
        combinedFilters,
        sort
      );
    });
  };

  const {
    page,
    pageSize,
    rowCount,
    pageCount,
    search,
    getPageData,
    onPageSizeChange,
    onPageChange,
    onSortChange,
    onSearchChange,
  } = usePagination({
    setData: setAccountingTransactions,
    getPageData: fetchData,
    mode: PAGINATION.SERVER,
  });

  useEffect(() => {
    getTotalSum();
  }, [search]);

  const renderDate = (val, dateFormat) => {
    return val ? moment(val).utc().format(dateFormat) : "";
  };

  const saveTabFilters = () => {
    switch (activeTab) {
      case ACCOUNTPAYABLE_ACTIVETAB_VALUE.OPENBILLS:
        setOpenBillsTabFilterState({
          searchTerm: search,
          statusFilter: selectedStatus,
          tableFilters: filterState,
        });
        break;
      case ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY:
        setHistoryTabFilterState({
          searchTerm: search,
          statusFilter: selectedStatus,
          tableFilters: filterState,
        });
        break;
      default:
        break;
    }
  };

  const getGroupedVals = (params) => {
    return params.value;
  };
  const formatCurrency = (value) => {
    if (!value) {
      return null;
    }
    if (value < 0) {
      return `-$${Math.abs(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${value?.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const STATUS_COLORS = {
    Open: theme.palette.primary.purple,
    Paid: theme.palette.primary.green,
    Processing: theme.palette.primary.orange,
    Pending: theme.palette.primary.blue,
    Payable: theme.palette.primary.teal,
    Cancelled: theme.palette.primary.grey,
    Withheld: theme.palette.primary.purple,
  };

  const renderStatusChip = (value) => {
    if (!value) {
      return null;
    }

    const color = STATUS_COLORS[value] || theme.palette.primary.blue;
    return (
      <Chip
        text={value}
        style={{
          display: "flex",
          borderRadius: 100,
          minWidth: "90px",
          justifyContent: "center",
        }}
        color={alpha(color, 0.15)}
        textColor={color}
      />
    );
  };

  const mainColumns = [
    {
      field: "displayNumber",
      headerName: "ID",
      width: 50,
      aggregable: false,
      renderCell: ({ value }) => {
        if (!value) {
          return null;
        }
        const parsedValue = parseInt(value);
        return (
          <MiLink
            style={{
              color: theme.palette.primary.blue,
              padding: 0,
              margin: 0,
            }}
            onClick={() =>
              openInNewTab(`/accounting/accountpayable/bills/${parsedValue}`)
            }
            title={`${parsedValue}`}
          />
        );
      },
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      width: 200,
      aggregable: false,
      groupingValueGetter: (params) => getGroupedVals(params),
    },
    {
      field: "payableToUserName",
      headerName: "Subcontractor",
      leafField: "address",
      flex: 1,
      aggregable: false,
      groupingValueGetter: (params) => getGroupedVals(params),
    },
    {
      field: "payableToUserBillingAccount",
      headerName: "Billing Account",
      flex: 1,
      aggregable: false,
    },
    {
      field: "expenseType",
      headerName: "Pay Type",
      flex: 1,
      aggregable: false,
    },
    {
      field: "walkTypeName",
      headerName: "Job Type",
      flex: 1,
      aggregable: false,
      renderCell: ({ value, row }) => {
        return (
          <div
            style={{
              fontSize: "small",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}{" "}
            {row.stageWalkProgress
              ? `${Math.round(row.stageWalkProgress * 100)}%`
              : ""}
          </div>
        );
      },
    },
    {
      field: "stageStatus",
      headerName: "Stage",
      flex: 1,
      aggregable: false,
    },
    {
      field: "stageScheduledStartDate",
      headerName: "Scheduled Date",
      flex: 1,
      aggregable: false,
      valueGetter: ({ value }) => renderDate(value, "MM/DD/YYYY"),
    },
    {
      field: "payableDate",
      headerName: "Earned",
      aggregable: false,
      valueGetter: ({ value }) => renderDate(value, "MM/DD/YYYY"),
    },
    {
      field: "payDate",
      headerName: "Pay Date",
      aggregable: false,
      valueGetter: ({ value }) =>
        value ? renderDate(value, "MM/DD/YYYY") : null,
    },
    {
      field: "exportedOnDate",
      headerName: "Exported Date",
      flex: 1,
      aggregable: false,
      valueGetter: ({ value }) => renderDate(value, "MM/DD/YYYY"),
    },
    {
      field: "amount",
      headerName: "Amount",
      type: "number",
      groupable: false,
      headerAlign: "left",
      renderCell: ({ value }) => {
        if (!value) {
          return null;
        }
        return (
          <div
            style={{
              fontWeight: MiTHEME.fontWeight.bold,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formatCurrency(value)}
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      aggregable: false,
      renderCell: ({ value }) => renderStatusChip(value),
    },
    {
      field: "",
      renderHeader: () => (
        <MiIcon path={mdiArrowRight} color={"green"} onClick={selectAll} />
      ),
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (param) => {
        const isSelected = selectedRows.some(
          (selRow) => selRow.id === param.row.id
        );
        if (isSelected) {
          return (
            <MiIconButton
              onClick={() =>
                Object.keys(param.row).length
                  ? removeFromDrawer(param.row)
                  : removeAllFromDrawer(param.rowNode)
              }
            >
              <MiIcon path={mdiArrowLeft} color={"red"} />
            </MiIconButton>
          );
        }
        return (
          <MiIconButton
            onClick={() =>
              Object.keys(param.row).length
                ? addToDrawer(param.row)
                : addAllToDrawer(param.rowNode)
            }
          >
            <MiIcon path={mdiArrowRight} color={"green"} />
          </MiIconButton>
        );
      },
    },
  ];

  const addToDrawer = (row) => {
    setSelectedRows([...selectedRows, row]);
  };

  const addAllToDrawer = (node) => {
    const rowsToAdd = accountingTransactions.filter(
      (e) => e[node.groupingField] === node.groupingKey
    );
    setSelectedRows([...new Set([...selectedRows, ...rowsToAdd])]);
  };

  const removeFromDrawer = (row) => {
    setSelectedRows(selectedRows.filter((e) => e.id !== row.id));
  };

  const removeAllFromDrawer = (node) => {
    const rowsToRemove = selectedRows.filter(
      (e) => e[node.groupingField] === node.groupingKey
    );
    setSelectedRows(selectedRows.filter((e) => !rowsToRemove.includes(e)));
  };

  const selectAll = () => {
    setSelectedRows(accountingTransactions);
  };

  const handleExportChipSelectionChange = (itm) => {
    setSelectedExportChip(itm);

    if (!itm.length || itm.length === 2) {
      exportFilter.removeField("exportedOnDate");
    } else if (itm[0].value === EXPORT_CHIP_OPTIONS.EXPORTED) {
      exportFilter.lessThanOrEqual({
        exportedOnDate: moment().toISOString(),
      });
    } else if (itm[0].value === EXPORT_CHIP_OPTIONS.NOT_EXPORTED) {
      exportFilter.equals({
        exportedOnDate: null,
      });
    }
  };

  const handleFilterChange = (filterCondition) => {
    setFilterState(filterCondition);
  };
  const handleSearchFilter = (searchTerm) => {
    if (searchTerm) {
      onSearchChange(searchTerm);
    } else {
      handleClearSearch();
    }
  };

  const openExportModal = (type) => {
    setSelectedExport(type);
    setIsExportModalOpen(true);
  };

  const closeExportModal = () => {
    setIsExportModalOpen(false);
  };

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
  });

  const renderTable = () => {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <AccountingFilterComponent
          handleExportButtonClick={openExportModal}
          handleButtonGroupClick={handleButtonGroupClick}
          handlePayButtonGroupClick={handlePayButtonGroupClick}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          handleExportChipSelectionChange={handleExportChipSelectionChange}
          activeTab={activeTab}
          selectedButton={selectedButton}
          paySelection={paySelection}
          selectedExportChip={selectedExportChip}
          searchTerm={search}
          handleSearch={handleSearchFilter}
          handleClearSearch={handleClearSearch}
          payPeriods={payPeriods}
          selectedMultiPayPeriod={selectedMultiPayPeriod}
          setSelectedMultiPayPeriod={setSelectedMultiPayPeriod}
        />
        <div style={{ flex: 1 }}>
          <div
            className={classes.headingWrapper}
            style={{
              background: theme.palette.lightAccent.grey,
              padding: "21px 10px 0 10px",
            }}
          >
            <div className={classes.heading}>{rowCount} Entries</div>
            <div className={classes.heading}>{formatCurrency(totalSum)}</div>
          </div>
          <div
            style={{
              height: "100vh",
              background: "white",
              borderRadius: 10,
              padding: "20px 5px 40px 10px",
            }}
          >
            <MiTable
              columns={mainColumns}
              rows={accountingTransactions}
              page={page}
              pages={pageCount}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              onPageChange={onPageChange}
              rowCount={rowCount}
              onSortModelChange={onSortChange}
              pagination
              paginationMode={PAGINATION.SERVER}
              sortingMode={PAGINATION.SERVER}
              filterMode={PAGINATION.SERVER}
              disableColumnPinning={true}
              defaultGroupingExpansionDepth={-1}
              initialState={initialState}
              apiRef={apiRef}
              getRowClassName={(params) => getClassName(params)}
              experimentalFeatures={{
                aggregation: true,
              }}
              onFilterModelChange={handleFilterChange}
              disableRowGrouping={true}
              rowGroupingModel={rowGroupingModel}
              aggregationModel={aggregationModel}
              onRowGroupingModelChange={(model) => {
                setRowGroupingModel(model);
                if (model.length === 0) {
                  setSelectedButton("");
                }
              }}
              filterModel={filterState}
              emptyListProps={{
                icon: mdiFormatListBulleted,
                text: "There are no records to display",
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleTabChange = (val) => {
    saveTabFilters();
    setActiveTab(val);
    setTotalSum(0);

    if (val === ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY) {
      setAccountingTransactions([]);
      setFilterState({ ...historyTabFilterState.tableFilters });
      setSelectedStatus(historyTabFilterState.statusFilter);
      onSearchChange(historyTabFilterState.searchTerm);
      return;
    }

    if (val === ACCOUNTPAYABLE_ACTIVETAB_VALUE.OPENBILLS) {
      setAccountingTransactions([]);
      setFilterState({ ...openBillsTabFilterState.tableFilters });
      setSelectedStatus(openBillsTabFilterState.statusFilter);
      onSearchChange(openBillsTabFilterState.searchTerm);
    }
  };

  const getClassName = (params) => {
    if (
      !params.row?.id &&
      params?.id &&
      params?.id.includes("auto-generated")
    ) {
      return "groupedRow";
    }
    return "";
  };

  const handleClearSearch = () => {
    onSearchChange("");
    if (activeTab === ACCOUNTPAYABLE_ACTIVETAB_VALUE.HISTORY) {
      setAccountingTransactions([]);
    }
  };

  const handleButtonGroupClick = (itm) => {
    setSelectedButton(itm.value);
    const mapping = {
      Subcontractors: "payableToUserName",
      Properties: "address",
    };
    setRowGroupingModel([mapping[itm.value]]);
  };

  const handlePayButtonGroupClick = (itm) => {
    setPaySelection(itm.value);
    exportFilter.equals({
      expenseType: payTypes[itm.label],
    });
  };

  return (
    <MiBox padding={0}>
      <ExportModal
        open={isExportModalOpen}
        onClose={closeExportModal}
        selected={selectedExport}
      />
      <div style={{ height: "100%", display: "flex" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <MiPageHeader
            title="Accounts Payable"
            color={MiTHEME.colors.teal.primary}
            hasDivider
            loading={stateManager.isBusy()}
          />
          <MiTab
            tabs={ACCOUNTPAYABLE_TAB_DATA}
            currenttab={activeTab}
            onTabChange={handleTabChange}
            renderTabContent={renderTable}
          />
        </div>
        <PayBillsCollapse
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          removeFromDrawer={removeFromDrawer}
          removeAllFromDrawer={removeAllFromDrawer}
          formatCurrency={formatCurrency}
          fetchData={getPageData}
        />
        <PrintPage rows={selectedRows} formatCurrency={formatCurrency} />
      </div>
    </MiBox>
  );
};

MiJournalEntries.propTypes = {
  /**
   * Styles applied to the Table Heading.
   * Ex:{color: theme.palette.primary.red,fontWeight: MiTHEME.fontWeight.bold}
   */
  tableHeadingStyles: PropTypes.object,
  /**
   * Background color when the accordion is expanded.Prop will be
   * passed to MiAccordion Component.
   */
  accordionActiveColor: PropTypes.string,
  /**
   * expand icon position.
   * Prop will be passed to MiAccordion Component.
   */
  iconPosition: PropTypes.oneOf(["right", "left"]),
  /**
   * Prop will be passed to MiAccordion Component.
   * gets accordionItem active state as callback value and should return appropriate expand icon
   *
   * Ex: ``` (isActive) => isActive?  <ArrowUpwardOutlined/> : <ArrowDownwardOutlined /> ```
   */
  renderExpandIcons: PropTypes.func,
};
